/**
 * Make It project JSON schema, version 1.
 *
 * This is the canonical, platform-neutral representation of a project. The web
 * player reads it from Firestore; the Unity → JSON converter produces it; and,
 * later on, the Unity app itself will read and write it.
 *
 * Conventions
 *  - Enums are string names (see enums.ts), never numbers.
 *  - Geometry: Unity UI coordinates on a 2048×1536 reference canvas, anchored
 *    at the top-left corner (pivot 0,1), so `x` grows to the right and `y` is
 *    negative downwards. Sizes are in the same units. Rotation is Euler degrees.
 *  - Media files are referenced by name: library assets by their path inside
 *    the shared library ("Letters/letters-01"), project files by their file
 *    name inside the project folder.
 *  - Object references of the C# model (DragElement.container, SequencesImg
 *    option) are expressed as element ids (`containerId`, `optionId`).
 */
import { z } from "zod";
import { FREE_ELEMENT_TYPE_VALUES, MATH_OPERATION_VALUES, MATH_TO_GUESS_VALUES, MEDIA_SOURCE_VALUES, OWNER_VALUES, POSITIVE_REINFORCEMENT_VALUES, PROJECT_TYPE_VALUES, SIMBOL_GROUP_VALUES, TEMPLATE_VALUES, TEXT_ALIGN_VALUES, TEXT_SIZE_VALUES, TIMER_TYPE_VALUES, } from "./enums.js";
export const SCHEMA_VERSION = 1;
// ---------------------------------------------------------------------------
// Leaf values
// ---------------------------------------------------------------------------
export const MediaSchema = z.object({
    source: z.enum(MEDIA_SOURCE_VALUES),
    /** Library path (without extension) or file name inside the project folder. */
    fileName: z.string(),
});
export const TextSchema = z.object({
    content: z.string().default(""),
    /** CSS hex colour, e.g. "#000000". */
    color: z.string().default("#000000"),
    align: z.enum(TEXT_ALIGN_VALUES).default("middleCenter"),
    size: z.enum(TEXT_SIZE_VALUES).default("s"),
});
export const LayoutSchema = z.object({
    x: z.number().default(0),
    y: z.number().default(0),
    width: z.number().default(0),
    height: z.number().default(0),
    rotation: z.object({ x: z.number(), y: z.number(), z: z.number() }).default({ x: 0, y: 0, z: 0 }),
});
// ---------------------------------------------------------------------------
// Elements
// ---------------------------------------------------------------------------
export const ElementSchema = z.object({
    /** `BaseElement.identifier`: unique within the project (a counter, stored as string). */
    id: z.string(),
    image: MediaSchema.nullable().default(null),
    sound: MediaSchema.nullable().default(null),
    text: TextSchema.default({}),
    layout: LayoutSchema.default({}),
});
export const FreeElementSchema = ElementSchema.extend({
    kind: z.enum(FREE_ELEMENT_TYPE_VALUES),
    /** Index (0-based, in slide order) of the slide this element jumps to when tapped, or null. */
    link: z.number().int().nullable().default(null),
    /** Video URL (kind === "video"). */
    url: z.string().nullable().default(null),
});
export const QuizElementSchema = ElementSchema.extend({
    correct: z.boolean().default(false),
    /** trueFalse slides: whether this statement is true. */
    isTrue: z.boolean().default(false),
});
export const TapElementSchema = ElementSchema.extend({ correct: z.boolean().default(false) });
export const OrderElementSchema = ElementSchema.extend({ locked: z.boolean().default(false) });
export const DragElementSchema = ElementSchema.extend({
    /** id of the ContainerElement this item belongs to. */
    containerId: z.string().nullable().default(null),
});
export const ConnectElementSchema = DragElementSchema;
export const SequencesImgElementSchema = ElementSchema.extend({
    locked: z.boolean().default(false),
    /** id of the option element that is the right answer for this slot. */
    optionId: z.string().nullable().default(null),
});
export const PieceElementSchema = ElementSchema.extend({
    rect: z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }),
    pieceWidth: z.number().default(0),
    pieceX: z.number().int().default(0),
    pieceY: z.number().int().default(0),
});
export const DrawStrokePointSchema = z.object({
    /** true when the pen is down at this point (false = pen lifted / move). */
    down: z.boolean(),
    x: z.number(),
    y: z.number(),
    z: z.number().default(0),
});
export const DrawSimbolSchema = z.object({
    points: z.array(DrawStrokePointSchema).default([]),
    image: MediaSchema.nullable().default(null),
    group: z.enum(SIMBOL_GROUP_VALUES).default("numbers"),
    color: z.string().nullable().default(null),
});
export const SimbolDrawElementSchema = ElementSchema.extend({
    simbol: DrawSimbolSchema.nullable().default(null),
});
// ---------------------------------------------------------------------------
// Slides
// ---------------------------------------------------------------------------
const SlideBase = z.object({
    /** `BaseSlide.identificador`: unique within the project. */
    id: z.number().int(),
    /** Background image name (library) or null for the template default. */
    background: z.string().nullable().default(null),
    instructions: ElementSchema.nullable().default(null),
    freeElements: z.array(FreeElementSchema).default([]),
    /** Time limit in seconds (quizzes). */
    seconds: z.number().int().default(20),
    /** Image shown with the question (quizzes). */
    image: ElementSchema.nullable().default(null),
});
const withType = (type, shape) => SlideBase.extend({ type: z.literal(type), ...shape });
export const FreeSlideSchema = withType("free", {});
export const DragSlideSchema = withType("drag", {
    draggableElements: z.array(DragElementSchema).default([]),
    containerElements: z.array(ElementSchema).default([]),
});
export const ConnectSlideSchema = withType("connect", {
    connectElements: z.array(ConnectElementSchema).default([]),
    containerElements: z.array(ElementSchema).default([]),
});
export const MemorySlideSchema = withType("memory", {
    memory1Elements: z.array(ElementSchema).default([]),
    memory2Elements: z.array(ElementSchema).default([]),
});
export const QuizSlideSchema = withType("quiz", {
    quizElements: z.array(QuizElementSchema).default([]),
    question: ElementSchema.nullable().default(null),
    /** The slide does not end on a wrong answer. */
    alwaysCorrect: z.boolean().default(true),
    /** All correct answers must be selected. */
    selectAllCorrects: z.boolean().default(false),
});
export const QuizzesQuizSlideSchema = QuizSlideSchema.extend({ type: z.literal("quizzesQuiz") });
export const TrueFalseSlideSchema = QuizSlideSchema.extend({ type: z.literal("trueFalse") });
export const FillWithLettersSlideSchema = withType("fillWithLetters", {
    fillElement: ElementSchema.nullable().default(null),
    question: ElementSchema.nullable().default(null),
    alwaysCorrect: z.boolean().default(true),
});
export const TapItSlideSchema = withType("tapIt", {
    tapElements: z.array(TapElementSchema).default([]),
    alwaysCorrect: z.boolean().default(true),
    selectAllCorrects: z.boolean().default(true),
});
export const OrderSlideSchema = withType("order", {
    orderElements: z.array(OrderElementSchema).default([]),
});
export const WordSlideSchema = withType("word", {
    word: ElementSchema.nullable().default(null),
    fakeLetters: z.number().int().default(0),
});
export const PhraseSlideSchema = withType("phrase", {
    phrase: ElementSchema.nullable().default(null),
    phraseFakeElements: z.array(ElementSchema).default([]),
});
export const DragTextSlideSchema = withType("dragText", {
    draggableElements: z.array(ElementSchema).default([]),
});
export const TraceableSlideSchema = withType("traceable", {
    points: z.array(ElementSchema).default([]),
    showAllNumbers: z.boolean().default(true),
});
export const PuzzleSlideSchema = withType("puzzle", {
    selectedImage: ElementSchema.nullable().default(null),
    pieces: z.array(PieceElementSchema).default([]),
    numberOfPieces: z.number().int().default(0),
    grid: z.object({ x: z.number().int(), y: z.number().int() }).default({ x: 0, y: 0 }),
    metrics: z
        .object({
        marginH: z.number(),
        marginV: z.number(),
        scale: z.number(),
        pieceW: z.number(),
        pieceH: z.number(),
        newMarginH: z.number(),
        newMarginV: z.number(),
    })
        .nullable()
        .default(null),
});
export const PaintSlideSchema = withType("paint", {
    paintImage: ElementSchema.nullable().default(null),
    imageScale: z.number().default(1),
});
export const SimbolDrawSlideSchema = withType("simbolDraw", {
    drawElements: z.array(SimbolDrawElementSchema).default([]),
});
export const MathSlideSchema = withType("math", {
    element1: z.number().default(0),
    element2: z.number().default(0),
    result: z.number().default(0),
    operation: z.enum(MATH_OPERATION_VALUES).default("sum"),
    equal: z.enum(MATH_OPERATION_VALUES).default("sum"),
    answers: z.array(QuizElementSchema).default([]),
    toGuess: z.enum(MATH_TO_GUESS_VALUES).default("result"),
    random: z.boolean().default(true),
    rtl: z.boolean().default(false),
    alwaysCorrect: z.boolean().default(true),
});
export const TypeAnswerSlideSchema = withType("typeAnswer", {
    answers: z.array(QuizElementSchema).default([]),
    question: ElementSchema.nullable().default(null),
});
export const MissingLetterSlideSchema = withType("missingLetter", {
    word: ElementSchema.nullable().default(null),
    answers: z.array(QuizElementSchema).default([]),
    /** Index of the hidden letter inside the word. */
    missingLetter: z.number().int().default(0),
});
export const MissingWordSlideSchema = withType("missingWord", {
    phrase: ElementSchema.nullable().default(null),
    answers: z.array(QuizElementSchema).default([]),
    /** Index of the hidden word (split on spaces/newlines). */
    missingWord: z.number().int().default(0),
    random: z.boolean().default(false),
});
export const SequencesImgSlideSchema = withType("sequencesImg", {
    elements: z.array(SequencesImgElementSchema).default([]),
    options: z.array(SequencesImgElementSchema).default([]),
});
/** Slide types that exist in the C# enum but have no editor/player implementation. */
export const UnimplementedSlideSchema = SlideBase.extend({
    type: z.enum(["sort", "imageReveal", "fractions", "scale", "mole", "crossword"]),
    elements: z.array(SequencesImgElementSchema).default([]),
});
export const SlideSchema = z.discriminatedUnion("type", [
    FreeSlideSchema,
    DragSlideSchema,
    ConnectSlideSchema,
    MemorySlideSchema,
    QuizSlideSchema,
    QuizzesQuizSlideSchema,
    TrueFalseSlideSchema,
    FillWithLettersSlideSchema,
    TapItSlideSchema,
    OrderSlideSchema,
    WordSlideSchema,
    PhraseSlideSchema,
    DragTextSlideSchema,
    TraceableSlideSchema,
    PuzzleSlideSchema,
    PaintSlideSchema,
    SimbolDrawSlideSchema,
    MathSlideSchema,
    TypeAnswerSlideSchema,
    MissingLetterSlideSchema,
    MissingWordSlideSchema,
    SequencesImgSlideSchema,
    UnimplementedSlideSchema,
]);
// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------
export const ProjectSettingsSchema = z.object({
    randomOrderElements: z.boolean().default(true),
    randomSlides: z.boolean().default(false),
    /** Library music name or "none". */
    backgroundMusic: z.string().default("none"),
    /** Default seconds per slide (quizzes). */
    seconds: z.number().int().default(0),
    timer: z.enum(TIMER_TYPE_VALUES).default("none"),
    positiveReinforcement: z.enum(POSITIVE_REINFORCEMENT_VALUES).default("simple"),
    prEachSlide: z.boolean().default(true),
    prStarsShowScore: z.boolean().default(false),
    autoNavigation: z.boolean().default(true),
    font: z.string().nullable().default(null),
    muteSoundAtCorrectAnswer: z.boolean().default(false),
    muteSoundAtWrongAnswer: z.boolean().default(false),
    muteSoundCongratulations: z.boolean().default(false),
    muteFx: z.boolean().default(false),
    showRanking: z.boolean().default(true),
    hideScore: z.boolean().default(false),
    allowRepeat: z.boolean().default(false),
});
export const ProjectSharingSchema = z.object({
    shareCode: z.number().int().default(0),
    isPublic: z.boolean().default(false),
    allowCloning: z.boolean().default(true),
    owner: z.enum(OWNER_VALUES).default("me"),
    ownerId: z.string().nullable().default(null),
    ownerName: z.string().nullable().default(null),
});
export const ProjectSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    /** `Project.identificador`: 25-char random code, also the folder name. */
    id: z.string().min(1),
    name: z.string(),
    type: z.enum(PROJECT_TYPE_VALUES),
    template: z.enum(TEMPLATE_VALUES).default("none"),
    /** ISO 639-1 language code of the content, or "unknown". */
    language: z.string().default("unknown"),
    settings: ProjectSettingsSchema.default({}),
    sharing: ProjectSharingSchema.default({}),
    /** Next free element id (counter used by the editor). */
    nextElementId: z.number().int().default(0),
    /** Unix seconds of last update in the Unity app (0 when never synced). */
    lastUpdateTime: z.number().int().default(0),
    /** Version of the Unity model the project was last saved with. */
    makeitVersion: z.number().int().default(0),
    slides: z.array(SlideSchema).default([]),
});
/** Parses and normalises an arbitrary value into a Project, throwing on error. */
export function parseProject(value) {
    return ProjectSchema.parse(value);
}
/** Safe variant: returns `{ success, data | error }`. */
export function safeParseProject(value) {
    return ProjectSchema.safeParse(value);
}
//# sourceMappingURL=project.js.map